// --- types
import type {
  AnyEventObject,
  MachineOptions,
  StateMachine,
  StateNodesConfig
} from "xstate";
import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded
} from "vue-router";
import type { ResponseError } from "../../utils";
export { QUERY_PARAMS } from "@upmind-automation/types";
// -----------------------------------------------------------------------------

/**
 * Enumeration representing various states or conditions that may require an action to be taken by the user or system.
 * This is often used in contexts like product configuration, order validation, or resource management.
 *
 * @enum {string}
 */
export enum REQUIRES_ACTION {
  /**
   * Indicates that an action is pending and has not yet been completed.
   * For example, a product configuration awaiting user input or a service provisioning awaiting completion.
   */
  PENDING = "pending",
  /**
   * Indicates that the current state or configuration is invalid and corrective action is required.
   * For example, missing required fields in a form or an incompatible product selection.
   */
  INVALID = "invalid",
  /**
   * Indicates that the state is related to another entity or process and may require attention.
   * For example, a product might require action if a related domain or hosting service has an issue.
   */
  RELATED = "related"
}

/**
 * Interface representing details about a page route, typically including both the target route
 * and the route from which the navigation originated.
 */
export type PageRoute = {
  /**
   * Details about the target route.
   */
  to?: RouteLocationNormalized;
  /**
   * Details about the route from which navigation originated.
   */
  from?: RouteLocationNormalizedLoaded;
};

export type FunnelProps = {
  id: string;
  states: StateNodesConfig<
    FunnelContext,
    StateMachine<FunnelContext, any, AnyEventObject>["schema"],
    any
  >;
  context?: FunnelContext;
  guards?: MachineOptions<FunnelContext, AnyEventObject>["guards"];
  services?: MachineOptions<FunnelContext, AnyEventObject>["services"];
  actions?: MachineOptions<FunnelContext, AnyEventObject>["actions"];
};

export type Funnels = Record<string, FunnelProps>;
/**
 * Interface representing the context for the routing engine, typically managed by an XState machine.
 * It holds the state of active flows, current route, and references to other services.
 */
export type RoutingEngineContext = {
  /**
   * A dictionary of all available funnels, keyed by their unique IDs.
   * The values of the dictionary are the funnel configurations used in the Factory function used to create funnel machines.
   */
  funnels: Funnels;

  /**
   * The actual funnel machine instance currently in use.
   * This is interpreted from the funnel configuration, based on the current funnel (ID).
   */
  funnel?: StateMachine<FunnelContext, any, AnyEventObject, any>;

  /**
   * The ID of the default funnel to fall back to when no specific funnel is active or provided.
   */
  defaultFunnel: string;

  /**
   * The target route that the routing engine is attempting to resolve or navigate to upon funnel invocation.
   */
  targetRoute?: FunnelTarget;

  /**
   * The ID of the currently active funnel being processed by the routing engine.
   */
  currentFunnel: string;

  // ---
  /**
   * An error object encountered by the routing engine.
   */
  error?: ResponseError;
  // ---
};

/**
 * Interface representing the context for the funnel, typically managed by an XState machine.
 * It holds the state of active flows, current route, and references to other services.
 */
export type FunnelContext = {
  /**
   * The current route object being managed by the funnel.
   */
  currentRoute?: RouteLocation;

  /**
   * The target route that the funnel is attempting to resolve or navigate to.
   */
  targetRoute?: FunnelTarget;

  /**
   * A boolean indicating whether the funnel has completed its resolution process.
   */
  resolved?: boolean;

  // ---
  /**
   * An error object encountered by the funnel.
   */
  error?: ResponseError;
};

export type FunnelTarget = {
  name?: RouteLocationNormalized["name"];
  params?: RouteLocationNormalized["params"];
  query?: RouteLocationNormalized["query"];
  hash?: RouteLocationNormalized["hash"];
  meta?: RouteLocationNormalized["meta"];
};

export type FunnelResponse = {
  type?: "NEXT" | "BACK";
  target?: FunnelTarget;
};
